import {
  type AutoBatchOptions,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';

import { baseApi } from './api/baseApi';
import { errorLoggerMiddleware } from './middleware/errorLogger';
import { negotiationConflictHandler } from './middleware/negotiationConflictHandler';
import commonReducer from './slices/commonSlice';
import transactionsReducer from './slices/transactionsSlice';
import userReducer from './slices/userSlice';

const rootReducer = combineReducers({
  common: commonReducer,
  user: userReducer,
  transactions: transactionsReducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

type StoreOptions = {
  /**
   * How the store batches the notifications of RTK Query's batched actions. Leave it unset for
   * RTK's default, which notifies on the next animation frame. Tests pass a timer instead; see
   * `tests/testStore.ts`.
   */
  autoBatch?: AutoBatchOptions;
};

export const setupStore = (
  preloadedState?: Partial<RootState>,
  { autoBatch }: StoreOptions = {}
) => {
  return configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(
        baseApi.middleware,
        errorLoggerMiddleware,
        negotiationConflictHandler
      ),
    preloadedState,
    enhancers: getDefaultEnhancers =>
      getDefaultEnhancers(autoBatch ? { autoBatch } : undefined),
  });
};

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
